import { hexToRgba, type Rgba } from "./rgba";

/**
 * `rgb(...)` / `rgba(...)` in any spacing. Legacy comma syntax only — that is
 * what the browser hands back from `getComputedStyle` and what
 * {@link formatRgba} emits, so the space-separated CSS Color 4 form never
 * reaches us.
 */
const RGB_FUNC = /rgba?\(([^)]*)\)/gi;

/** `#abc`, `#abcd`, `#aabbcc`, `#aabbccdd` — longest form first so 8 wins over 6. */
const HEX = /#(?:[0-9a-f]{8}|[0-9a-f]{6}|[0-9a-f]{4}|[0-9a-f]{3})\b/gi;

const toToken = ({ r, g, b, a }: Rgba): string =>
  `${Math.round(r)},${Math.round(g)},${Math.round(b)},${Math.round(a * 255)}`;

/**
 * Rewrite every color literal in `value` to a single canonical `r,g,b,a` token
 * and collapse the surrounding whitespace/case.
 *
 * Runs over the whole string rather than the whole value, so a solid
 * (`#FFF`) and a gradient (`linear-gradient(90deg, #FFF 0%, ...)`) go through
 * the same code path and gradients compare stop-by-stop instead of by raw
 * string identity. Anything that isn't a color literal — `var(--theme-color-1)`,
 * `90deg`, `circle at center`, `0%` — is left alone and only lowercased, so
 * two different theme variables never collapse onto each other.
 */
const canonicalize = (value: string): string =>
  value
    .replace(RGB_FUNC, (match, args: string) => {
      // Percentage channels would parse as their bare number: `rgb(100%, 0%, 0%)`
      // (red) and `rgb(100, 0, 0)` (dark red) would canonicalize alike and ring
      // the wrong swatch. Nothing in the editor emits them, so leave the literal
      // alone rather than grow a second unit path.
      if (args.includes("%")) return match;
      const parts = args.split(",").map((part) => Number.parseFloat(part));
      if (parts.length < 3 || parts.slice(0, 3).some(Number.isNaN))
        return match;
      const [r, g, b, a] = parts;
      return toToken({ r, g, b, a: Number.isFinite(a) ? a : 1 });
    })
    .replace(HEX, (match) => toToken(hexToRgba(match)))
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

/**
 * Whether two color values paint the same thing.
 *
 * `#FFF`, `#ffffff` and `rgb(255, 255, 255)` are one color, and the editor
 * mixes all three: the picker persists uppercase 6-char hex, Froala writes
 * inline `rgb(...)`, and `TextColorSetting` converts that to lowercase hex. A
 * raw `===` between the applied value and a palette swatch therefore misses,
 * which is why a palette of near-identical shades shows no selection at all.
 *
 * Empty values are never equal to anything — "no color" is the Transparent
 * row's job, not a swatch's.
 */
export const isSameColor = (a?: string | null, b?: string | null): boolean => {
  if (!a || !b) return false;
  if (a === b) return true;
  return canonicalize(a) === canonicalize(b);
};
